import { useMemo } from 'react';
import { Phone } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { GROUP_META, compareServerName, getGroup } from '../helpers';
import type { ServerGroup, ServerStatsRow } from '../types';

interface Props {
  servers: ServerStatsRow[];
  isLoading: boolean;
}

interface AnnotatedServer extends ServerStatsRow {
  _group: ServerGroup;
}

export function DialerCallsTab({ servers, isLoading }: Props) {
  const dialers: AnnotatedServer[] = useMemo(
    () =>
      servers
        .filter((s) => {
          const n = (s.serverName ?? '').toUpperCase();
          return n.includes('_AHM') || n.includes('_BLR');
        })
        .map((s) => ({ ...s, _group: getGroup(s.serverName) }))
        .sort((a, b) => compareServerName(a.serverName, b.serverName)),
    [servers],
  );

  const ahm = dialers.filter((s) => s._group === 'ahm');
  const blr = dialers.filter((s) => s._group === 'blr');
  const totalAhm = ahm.reduce((sum, s) => sum + (s.activeCalls ?? 0), 0);
  const totalBlr = blr.reduce((sum, s) => sum + (s.activeCalls ?? 0), 0);
  const grand = totalAhm + totalBlr;

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-44" />
        <Skeleton className="h-44" />
      </div>
    );
  }

  if (dialers.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card/50 py-12 text-center text-sm text-muted-foreground">
        No dialer servers reporting.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Grand total banner */}
      <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Total Active Calls
          </div>
          <div className="text-3xl font-semibold tabular-nums text-foreground">
            {grand.toLocaleString()}
          </div>
        </div>
        <div className="space-y-0.5 text-right text-xs text-muted-foreground">
          <div>
            AHM:{' '}
            <span className="font-mono font-semibold text-foreground">
              {totalAhm.toLocaleString()}
            </span>
          </div>
          <div>
            BLR:{' '}
            <span className="font-mono font-semibold text-foreground">
              {totalBlr.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <CallsGroup
        label={GROUP_META.ahm.label}
        total={totalAhm}
        servers={ahm}
      />
      <CallsGroup
        label={GROUP_META.blr.label}
        total={totalBlr}
        servers={blr}
      />
    </div>
  );
}

function CallsGroup({
  label,
  total,
  servers,
}: {
  label: string;
  total: number;
  servers: ServerStatsRow[];
}) {
  if (servers.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
          {label}
        </h3>
        <span className="text-[11px] text-muted-foreground">
          {total.toLocaleString()} total calls · {servers.length} server
          {servers.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {servers.map((s) => {
          const active = (s.activeCalls ?? 0) > 0;
          return (
            <div
              key={s.serverName}
              className={cn(
                'flex items-center gap-3 rounded-md border bg-card px-3 py-2 shadow-sm',
                active
                  ? 'border-emerald-300'
                  : 'border-border',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {s.serverName}
                </div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">
                  {s.ip}
                </div>
              </div>
              <div className="text-right">
                <div
                  className={cn(
                    'flex items-center justify-end gap-1 font-mono text-lg font-semibold tabular-nums',
                    active ? 'text-emerald-700' : 'text-muted-foreground',
                  )}
                >
                  <Phone className="h-3.5 w-3.5" />
                  {(s.activeCalls ?? 0).toLocaleString()}
                </div>
                <div className="text-[10px] uppercase text-muted-foreground">
                  calls
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
